import React from 'react';

interface AsyncState<TData, TError extends Error> {
  loading: boolean;
  error: TError | null;
  data: TData | null;
}

interface Config<TData, TError extends Error> {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
}

export function useMutate<TArgs, TData, TError extends Error>(
  func: (args: TArgs) => Promise<TData>,
  { onSuccess, onError }: Config<TData, TError> = {}
): AsyncState<TData, TError> & { execute: (args: TArgs) => void } {
  const [state, setState] = React.useState<AsyncState<TData, TError>>({ loading: false, error: null, data: null });

  const execute = React.useCallback(
    async (args: TArgs) => {
      setState(prev => ({ loading: true, error: null, data: null }));
      try {
        const data = await func(args);
        setState(prev => ({ ...prev, loading: false, data }));
        onSuccess && onSuccess(data);
      } catch (e) {
        setState(prev => ({ ...prev, loading: false, error: e as TError }));
        onError && onError(e as TError);
      }
    },
    [func, setState, onSuccess, onError]
  );
  return {
    ...state,
    execute,
  };
}
